import React, {useState, useEffect, useRef} from "react";
import Calendar from "./Calendar";
import "../styles/BookingWidget.css";
import moment from "moment";
import AvailabilityBadge from "./AvailabilityBadge";
import {
    Button,
    ButtonIcon,
    ButtonType,
    IconCalendar,
    IconCheck,
    IconGlobe,
    IconGoogleMeet,
    IconTime,
    IconUsTwo
} from "./Button";
import {Config} from "../Config";

interface BookingWidgetProps {
    availableTimes: Record<string, string[]>; // e.g., { "2025-06-10": ["09:00", "09:15"] }
    availableDays: string[]; // e.g., ["2025-06-12"]
    fullyBookedTimes: Record<string, string[]>; // e.g., { "2025-06-10": ["09:15"] }
    onSubmit: (info: {
        date: string;
        time: string;
        name: string;
        email: string;
        note: string;
        source?: string;
    }) => Promise<boolean>;
}

const BookingWidget: React.FC<BookingWidgetProps> = ({
                                                         availableTimes,
                                                         availableDays,
                                                         fullyBookedTimes,
                                                         onSubmit,
                                                     }) => {
    const [selectedDate, setSelectedDate] = useState<string>("");
    const [selectedTime, setSelectedTime] = useState<string>("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [note, setNote] = useState("");
    const [source, setSource] = useState(null);
    const [step, setStep] = useState<"calendar" | "details" | "confirmation" | "confirmation-error">("calendar");

    const calendarAndTimeRef= useRef(null);
    const selectTimeRef     = useRef(null);
    const bookingFormRef    = useRef(null);
    const contactNameRef    = useRef(null);

    const isTimeUnavailable = (date: string, time: string) => fullyBookedTimes[date]?.includes(time);


    useEffect(() => {
        if(window.innerWidth <= 992) {
            if(step === "calendar" && selectedDate) {
                calendarAndTimeRef.current && calendarAndTimeRef.current.scrollIntoView({ behavior: "smooth", block: "start", container: "nearest" });
            } else if(step === "details") {
                calendarAndTimeRef.current && calendarAndTimeRef.current.scrollIntoView({ behavior: "smooth", block: "start", container: "nearest" });
            }
        }
    }, [selectedDate])

    useEffect(() => {
        if(window.innerWidth <= 992) {
            if(step === "details" && selectedDate && selectedTime) {
                bookingFormRef.current && bookingFormRef.current.scrollIntoView({ behavior: "smooth", block: "start", container: "nearest" });

                contactNameRef.current && contactNameRef.current.focus();
            }
        }
    }, [selectedTime])

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSubmit({ date: selectedDate, time: selectedTime, name, email, note, source }).then((result : boolean) => {
            if(result) {
                setStep("confirmation");
            } else {
                setStep("confirmation-error");
            }
        })
    };

    const availableTimeSlots = selectedDate
        ? availableTimes[selectedDate]?.filter((time) => !isTimeUnavailable(selectedDate, time)) || []
        : [];


    return (
        <div className={"booking-widget section"} id={Config.PageSections.Booking}>
            <AvailabilityBadge label={"Booking for " + (moment().date() > 25 ? moment().add(1, "month").format("MMMM") : moment().format("MMMM"))} className={"animate-from-bottom"}/>
            <h2 className={"animate-from-bottom delay-1"}>
                Let’s talk about your project!
            </h2>
            <p className="subtitle animate-from-bottom delay-2">
                Book a strategic 20-min call with us and let’s uncover what your website truly needs!
            </p>
            <div
                className={"booking-wrapper" + ((!selectedDate && step === "calendar") ? " animate-from-bottom delay-3" : "") + (selectedDate ? " expanded" : "") + (step !== "calendar" ? " rolled-up" : "")}
                ref={calendarAndTimeRef}
            >
                <>
                    <Calendar selectedDate={selectedDate}
                              onDateSelect={(date) => {
                                  setSelectedDate(date);
                                  setSelectedTime("");
                              }}
                              availableDays={availableDays}
                    />
                    <div className={"select-time" + (selectedDate ? " visible" : " hidden")} ref={selectTimeRef}>
                        <div className={"header"}>
                            <p className="date font-body-L">{moment(selectedDate).format("ddd")} <span
                                className={"date"}>{moment(selectedDate).format("DD MMMM")}</span></p>
                            <p className="font-body-S">Available time slots:</p>
                        </div>
                        {availableTimeSlots.length > 0 ? (
                            <div className="available-slots">
                                {availableTimes[selectedDate].map((time) => (
                                    <button
                                        key={time}
                                        disabled={isTimeUnavailable(selectedDate, time)}
                                        onClick={() => {
                                            setSelectedTime(time);
                                            setStep("details");
                                        }}
                                        className={`time ${
                                            selectedTime === time ? "selected" : ""
                                        }`}
                                    >
                                        {time}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p>No available time slots for this day.</p>
                        )}
                    </div >
                </>

            </div>
            <div
                className={"booking-wrapper" + (selectedDate ? " expanded" : "") + (step !== "details" ? " rolled-up" : "")} ref={bookingFormRef}>
                <div className="left-side">
                    <div className="header">
                        {IconUsTwo()}
                        <span className={"font-body-M"}>Mária & Michal @najsu code & design</span>
                    </div>
                    <div>
                        <h5>Discovery call (20-min)</h5>
                        <p className={"font-body-M"}>Let’s talk about your project! 👋</p>
                    </div>

                    <div className={"call-list"}>
                        <div className={"list-item font-body-M-Regular"}>{IconTime()} 20 min.</div>
                        <div className={"list-item font-body-M-Regular"}>{IconCalendar()} {moment(selectedDate).format("dddd, MMMM DD, YYYY") + ", " + selectedTime}</div>
                        <div className={"list-item font-body-M-Regular"}>{IconCheck()} Requires confirmation</div>
                        <div className={"list-item font-body-M-Regular"}>{IconGoogleMeet()} Video conference via Google Meet</div>
                        <div className={"list-item font-body-M-Regular"}>{IconGlobe()} Central European Time</div>
                    </div>
                </div>
                <div className="delimiter"></div>

                <form className="right-side" onSubmit={handleSubmit}>
                    <h5>Enter your details</h5>
                    <div>
                        <div className="label font-body-M">Your name*</div>
                        <input
                            type="text"
                            className="w-full border rounded p-2"
                            placeholder={"Peter Pan"}
                            value={name}
                            required={true}
                            aria-required="true"
                            ref={contactNameRef}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                    <div>
                        <div className="label font-body-M">Email address*</div>
                        <input
                            type="email"
                            className="w-full border rounded p-2"
                            placeholder={"peter.pan@mail.com"}
                            value={email}
                            required
                            aria-required="true"
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div>
                        <div className="label font-body-M">What do you need help with? Please share anything that will help
                            prepare for our meeting.</div>
                        <textarea
                            className="w-full border rounded p-2"
                            value={note}
                            required
                            aria-required="true"
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </div>
                    <div>
                        <div className="label font-body-M">How did you find us?</div>
                        <div className="radio-group">
                            <label className="radio-option">
                                <input type="radio" name="source" value="twitter" checked={source === "twitter"} onChange={(e) => setSource(e.target.value)}/>
                                <span className={"font-body-M-Regular"}>X (Twitter)</span>
                            </label>
                            <label className="radio-option">
                                <input type="radio" name="source" value="linkedin" checked={source === "linkedin"} onChange={(e) => setSource(e.target.value)}/>
                                <span className={"font-body-M-Regular"}>LinkedIn</span>
                            </label>
                            <label className="radio-option">
                                <input type="radio" name="source" value="google" checked={source === "google"} onChange={(e) => setSource(e.target.value)}/>
                                <span className={"font-body-M-Regular"}>Google</span>
                            </label>
                            <label className="radio-option">
                                <input type="radio" name="source" value="recommendation" checked={source === "recommendation"} onChange={(e) => setSource(e.target.value)}/>
                                <span className={"font-body-M-Regular"}>Personal recommendation</span>
                            </label>
                            <label className="radio-option">
                                <input type="radio" name="source" value="other" checked={source === "other"} onChange={(e) => setSource(e.target.value)}/>
                                <span className={"font-body-M-Regular"}>Other</span>
                            </label>
                        </div>
                    </div>
                    <p className={"font-body-S"}>By proceeding you agree to our <a
                        href={Config.PageLinks.TermsAndConditions} target={"_blank"}>Terms
                        & Conditions</a> and <a href={Config.PageLinks.PrivacyPolicy} target={"_blank"}>Privacy
                        Policy</a>.</p>
                    <div className={"buttons-wrapper"}>
                        <Button classes={["btn-clean btn-md"]} type={ButtonType.CallToAction} nativeType={"button"}
                                onClick={() => setStep("calendar")}>Back</Button>
                        <Button classes={["btn-light btn-md"]} type={ButtonType.CallToAction} nativeType={"submit"}
                                icon={ButtonIcon.Calendar} onClick={""}>Schedule the call</Button>
                    </div>
                </form>
            </div>

            <div className={"booking-wrapper confirmation" + (step !== "confirmation" ? " rolled-up" : "")}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M15.25 2H8.75C6.9606 2.00265 5.24528 2.71468 3.97998 3.97998C2.71468 5.24528 2.00265 6.9606 2 8.75V15.25C2.00265 17.0394 2.71468 18.7547 3.97998 20.02C5.24528 21.2853 6.9606 21.9974 8.75 22H15.25C17.0394 21.9974 18.7547 21.2853 20.02 20.02C21.2853 18.7547 21.9974 17.0394 22 15.25V8.75C21.9974 6.9606 21.2853 5.24528 20.02 3.97998C18.7547 2.71468 17.0394 2.00265 15.25 2ZM17.31 10.16L12.09 15.38C11.7167 15.7556 11.2096 15.9677 10.68 15.97C10.4193 15.97 10.1611 15.9191 9.91998 15.82C9.67995 15.7166 9.46256 15.5671 9.28003 15.38L6.69 12.79C6.52618 12.5987 6.4406 12.3526 6.45032 12.101C6.46004 11.8493 6.56434 11.6105 6.74243 11.4324C6.92053 11.2543 7.15927 11.15 7.41095 11.1403C7.66263 11.1305 7.90867 11.2162 8.09998 11.38L10.69 13.97L15.9 8.73999C15.9933 8.64741 16.1038 8.57408 16.2253 8.52423C16.3469 8.47438 16.4771 8.449 16.6085 8.44946C16.7399 8.44993 16.8699 8.47627 16.9911 8.52698C17.1124 8.57769 17.2224 8.65176 17.315 8.745C17.4076 8.83823 17.4809 8.9488 17.5308 9.07037C17.5806 9.19194 17.606 9.32216 17.6055 9.45355C17.6051 9.58495 17.5787 9.71497 17.528 9.83618C17.4773 9.9574 17.4032 10.0674 17.31 10.16Z"
                        fill="#2CBE4C"/>
                </svg>
                <h3 className="">Your booking request is in!</h3>
                <p>
                    You’ve requested a call on {moment(selectedDate).format("MMMM Do, YYYY")} at {selectedTime}.<br/> Please check your email to confirm your
                    booking.
                </p>
            </div>

            <div className={"booking-wrapper confirmation" + (step !== "confirmation-error" ? " rolled-up" : "")}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M11 0.75C8.9728 0.75 6.99109 1.35116 5.30548 2.47745C3.61988 3.60373 2.30601 5.20454 1.53022 7.07748C0.754423 8.95043 0.551463 11.0114 0.946963 12.9997C1.34246 14.988 2.31865 16.8143 3.75214 18.2478C5.18563 19.6813 7.012 20.6575 9.00033 21.053C10.9886 21.4485 13.0496 21.2456 14.9225 20.4698C16.7955 19.694 18.3963 18.3802 19.5226 16.6946C20.6489 15.009 21.25 13.0273 21.25 11C21.2474 8.28234 20.1666 5.67677 18.2449 3.7551C16.3232 1.83342 13.7177 0.75265 11 0.75ZM15.67 14.25C15.7637 14.343 15.8382 14.4536 15.8889 14.5754C15.9397 14.6973 15.9659 14.828 15.9659 14.96C15.9659 15.092 15.9397 15.2227 15.8889 15.3446C15.8382 15.4664 15.7637 15.5771 15.67 15.67C15.577 15.7637 15.4664 15.8381 15.3445 15.8889C15.2227 15.9397 15.092 15.9658 14.96 15.9658C14.828 15.9658 14.6973 15.9397 14.5755 15.8889C14.4536 15.8381 14.343 15.7637 14.25 15.67L11 12.42L7.75006 15.67C7.56176 15.857 7.30681 15.9615 7.04145 15.9606C6.77608 15.9596 6.522 15.8533 6.33502 15.665C6.14805 15.4767 6.04356 15.2218 6.0445 14.9565C6.04543 14.6911 6.15173 14.437 6.34003 14.25L9.59003 11L6.34003 7.75C6.23535 7.66035 6.1503 7.55003 6.09027 7.42596C6.03025 7.3019 5.99659 7.16677 5.99127 7.02905C5.98596 6.89133 6.00912 6.754 6.05939 6.62567C6.10966 6.49735 6.18593 6.38081 6.28339 6.28336C6.38084 6.1859 6.49735 6.10963 6.62567 6.05936C6.754 6.00908 6.8914 5.98589 7.02912 5.99121C7.16684 5.99653 7.3019 6.03025 7.42597 6.09027C7.55003 6.15029 7.66042 6.23531 7.75006 6.34L11 9.59L14.25 6.34C14.343 6.24627 14.4536 6.17189 14.5755 6.12112C14.6973 6.07036 14.828 6.04419 14.96 6.04419C15.092 6.04419 15.2227 6.07036 15.3445 6.12112C15.4664 6.17189 15.577 6.24627 15.67 6.34C15.8562 6.52736 15.9608 6.78083 15.9608 7.04501C15.9608 7.3092 15.8562 7.56264 15.67 7.75L12.42 11L15.67 14.25Z"
                        fill="#FF0000"/>
                </svg>
                <h3 className="">Your booking request was not sent.</h3>
                <p>
                    Something went wrong and your call wasn’t scheduled. <br/> Please try again or contact us if the issue continues.
                </p>
            </div>
        </div>
    );
};

export default BookingWidget;
